import { inferAdditionalFields, twoFactorClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { API_URL } from './api';

/** better-auth's browser client for the API's `/auth/*` routes (sends cookies cross-origin). */
export const authClient = createAuthClient({
  baseURL: API_URL,
  basePath: '/auth',
  plugins: [
    twoFactorClient(),
    // Sent on email sign-up as `YYYY-MM` (age gate).
    inferAdditionalFields({ user: { birthMonth: { type: 'string', required: false } } }),
  ],
});

/** Absolute URL on this site, for links the API redirects back to (emails, social sign-in). */
export function siteUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

/** A `?next=` path, if it stays on this site (so it can't send people to another website). */
export function returnPath(next: string | null): string | null {
  return next?.startsWith('/') && !next.startsWith('//') ? next : null;
}

/** `path`, carrying where to go back to after signing in or up. */
export function withNext(path: string, next: string | null): string {
  return next ? `${path}?next=${encodeURIComponent(next)}` : path;
}

/** better-auth error codes → what we tell people. Anything unlisted falls back to the server's message. */
const MESSAGES: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: 'That email and password don’t match.',
  EMAIL_NOT_VERIFIED: 'Confirm your email first. We’ve sent you a new link.',
  USER_ALREADY_EXISTS: 'An account with this email already exists. Try signing in.',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL:
    'An account with this email already exists. Try signing in.',
  PASSWORD_TOO_SHORT: 'Use at least 8 characters for your password.',
  PASSWORD_TOO_LONG: 'Use at most 128 characters for your password.',
  INVALID_PASSWORD: 'That password isn’t right.',
  INVALID_TOKEN: 'This link has expired or was already used. Ask for a new one.',
  INVALID_CODE: 'That code isn’t right. Check your app and try again.',
  INVALID_TWO_FACTOR_COOKIE: 'Your sign-in timed out. Start again.',
  ACCOUNT_BANNED: 'This account is suspended.',
  TOO_MANY_REQUESTS: 'Too many attempts. Wait a minute and try again.',
  MISSING_RESPONSE: 'Complete the “I’m human” check first.',
  VERIFICATION_FAILED: 'The “I’m human” check failed. Try it again.',
};

const FALLBACK = 'Something went wrong. Try again.';

export function authErrorMessage(
  error: { code?: string; message?: string; status?: number } | null,
): string {
  if (!error) return FALLBACK;
  if (error.status === 429) return MESSAGES.TOO_MANY_REQUESTS ?? FALLBACK;
  return (error.code && MESSAGES[error.code]) || error.message || FALLBACK;
}
